import { Link, useParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LandingNav from "@/components/LandingNav";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Check, ArrowRight } from "lucide-react";
import { featurePages, getFeaturePage } from "@/content/featurePages";

const FeatureLanding = () => {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const page = slug ? getFeaturePage(slug) : undefined;

  if (!page) return <Navigate to="/applications" replace />;

  const Icon = page.icon;
  const otherFeatures = featurePages.filter((p) => p.slug !== page.slug);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: page.h1,
      description: page.metaDescription,
      url: `https://testassets.io/features/${page.slug}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEOHead
        title={page.metaTitle}
        description={page.metaDescription}
        path={`/features/${page.slug}`}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: t("nav.start"), url: "/" },
          { name: t("features.indexTitle", "Features"), url: "/features" },
          { name: page.h1, url: `/features/${page.slug}` },
        ]}
      />
      <LandingNav />

      <main className="flex-1">
        {/* Hero */}
        <section
          className="relative overflow-hidden border-b border-border"
          aria-labelledby="feature-hero-heading"
        >
          <div
            className="absolute inset-0 opacity-60"
            style={{
              background:
                "radial-gradient(ellipse at top, hsl(var(--primary) / 0.15), transparent 60%)",
            }}
          />
          <div className="relative container mx-auto px-6 py-20 max-w-4xl text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/10 text-primary mb-6">
              <Icon className="h-6 w-6" />
            </div>
            <p className="text-sm font-medium text-primary mb-3 uppercase tracking-wider">
              {page.eyebrow}
            </p>
            <h1
              id="feature-hero-heading"
              className="text-4xl md:text-5xl font-bold text-foreground mb-5 leading-tight"
            >
              {page.h1}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {page.intro}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild variant="primary" size="lg">
                <Link to={page.primaryCta.to}>
                  {page.primaryCta.label}
                  <ArrowRight />
                </Link>
              </Button>
              {page.secondaryCta && (
                <Button asChild variant="outline" size="lg">
                  <Link to={page.secondaryCta.to}>{page.secondaryCta.label}</Link>
                </Button>
              )}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="container mx-auto px-6 py-16 max-w-5xl">
          <h2 className="text-3xl font-bold text-foreground text-center mb-10">
            {t("features.whyHeading", "Why teams choose this")}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {page.benefits.map((b) => (
              <Card key={b.title}>
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <CardTitle className="text-lg">{b.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="text-sm text-muted-foreground pl-14">
                  {b.description}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="border-t border-border bg-card/30">
          <div className="container mx-auto px-6 py-16 max-w-4xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-10">
              {t("features.howHeading", "How it works")}
            </h2>
            <ol className="space-y-6">
              {page.howItWorks.map((s, i) => (
                <li key={s.title} className="flex gap-4">
                  <div className="shrink-0 h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold">
                    {i + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{s.title}</h3>
                    <p className="text-sm text-muted-foreground">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Use cases */}
        <section className="container mx-auto px-6 py-16 max-w-4xl">
          <h2 className="text-3xl font-bold text-foreground text-center mb-10">
            {t("features.useCasesHeading", "Built for teams that...")}
          </h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {page.useCases.map((u) => (
              <li
                key={u}
                className="flex items-start gap-3 p-4 rounded-lg border border-border bg-card"
              >
                <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <span className="text-sm text-foreground">{u}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* FAQ */}
        <section className="border-t border-border bg-card/30">
          <div className="container mx-auto px-6 py-16 max-w-3xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-10">
              {t("features.faqHeading", "Frequently asked questions")}
            </h2>
            <Accordion type="single" collapsible>
              {page.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left">{f.q}</AccordionTrigger>
                  <AccordionContent>{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-6 py-16 max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            {t("features.ctaHeading", "Ready to try TestAssets?")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t(
              "features.ctaSubtitle",
              "Free to get started. No credit card required.",
            )}
          </p>
          <Button asChild variant="primary" size="lg">
            <Link to="/auth">
              {t("features.ctaButton", "Create your account")}
              <ArrowRight />
            </Link>
          </Button>
        </section>

        {/* Other features */}
        <section className="border-t border-border">
          <div className="container mx-auto px-6 py-12 max-w-5xl">
            <h2 className="text-xl font-semibold text-foreground mb-6">
              {t("features.exploreHeading", "Explore other features")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {otherFeatures.map((p) => {
                const FIcon = p.icon;
                return (
                  <Link
                    key={p.slug}
                    to={`/features/${p.slug}`}
                    className="group p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                  >
                    <FIcon className="h-5 w-5 text-primary mb-2" />
                    <h3 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors">
                      {p.h1}
                    </h3>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FeatureLanding;